import React, { useEffect, useState } from "react";
import { Input, Select, Table, TableProps, message } from "antd";
import { IInvoiceUserList } from "./types";
import { mapInvoiceUserList } from "./Mapper";
import { InvoiceService, InvoiceUserListParams } from "../../../services/Invoices/InvoiceService/InvoiceService";
import PageWrapper from "../../../Layout/PageWrapper";
import { getColumns } from "./Columns";
import useDebounce from "../../../hooks/useDebounce";
import Search from "antd/es/input/Search";

const { Option } = Select;

const InvoiceUserList: React.FC = () => {
	const [loading, setLoading] = useState<boolean>(false);
	const [invoiceUserList, setInvoiceUserList] = useState<IInvoiceUserList[]>([]);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [searchValue, setSearchValue] = useState<string>("");
	const [params, setParams] = useState<InvoiceUserListParams>({
		pageNumber: 1,
		pageSize: 10,
		searchTerm: "",
		sortField: "",
		sortType: "",
		roleNameFilter: ""
	});

	const debouncedSearchTerm = useDebounce(searchValue, 500);

	useEffect(() => {
		setParams((prev) => ({ ...prev, searchTerm: debouncedSearchTerm, pageNumber: 1 }));
	}, [debouncedSearchTerm]);

	useEffect(() => {
		getInvoiceUserList();
	}, [params]);

	const getInvoiceUserList = async () => {
		setLoading(true);
		try {
			// Filter out empty params
			const filteredParams = Object.fromEntries(Object.entries(params).filter(([_, value]) => value !== "" && value !== undefined));
			const res = await InvoiceService.getInvoiceUserList(filteredParams as InvoiceUserListParams);
			const mappedData = mapInvoiceUserList(res.data?.data || []);
			setInvoiceUserList(mappedData);
			setTotalRecords(res.data?.totalRecords || 0);
		} catch (error: any) {
			message.error(error?.response?.data?.message || "Something went wrong");
		} finally {
			setLoading(false);
		}
	};

	const handleTableChange: TableProps<IInvoiceUserList>["onChange"] = (pagination, filters, sorter) => {
		let sortField = "";
		let sortType = "";
		if (Array.isArray(sorter)) {
			// Handle multiple sorters if needed
		} else {
			sortField = (sorter.field as string) || "";
			sortType = sorter.order === "ascend" ? "asc" : sorter.order === "descend" ? "desc" : "";
		}
		const newParams = {
			...params,
			pageNumber: pagination.current || 1,
			pageSize: pagination.pageSize || 10,
			sortField,
			sortType
		};
		setParams(newParams);
	};

	const handleSearch = (value: string) => {
		setSearchValue(value);
	};

	const handleRoleFilter = (value: string) => {
		setParams({ ...params, roleNameFilter: value, pageNumber: 1 });
	};

	return (
		<PageWrapper title="Invoice User List">
			<div style={{ marginBottom: 16 }}>
				<Search
					placeholder="Search"
					onChange={(e) => setSearchValue(e.target.value)} // updates debounced value while typing
					onSearch={handleSearch}
					style={{ width: 300, marginRight: 16 }}
				/>
				<Select placeholder="Filter by role" onChange={handleRoleFilter} style={{ width: 200 }} allowClear>
					<Option value="Invoice Creator">Invoice Creator</Option>
					<Option value="Invoice Payer">Invoice Payer</Option>
					{/* Add more roles as needed */}
				</Select>
			</div>
			<Table<IInvoiceUserList>
				loading={loading}
				columns={getColumns()}
				dataSource={invoiceUserList}
				pagination={{
					current: params.pageNumber,
					pageSize: params.pageSize,
					total: totalRecords,
					showSizeChanger: true,
					showQuickJumper: true,
					showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
				}}
				onChange={handleTableChange}
				scroll={{ x: "max-content" }}
			/>
		</PageWrapper>
	);
};

export default InvoiceUserList;
